import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Divider,
    Chip,
    InputAdornment,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    TableRestaurant as TableRestaurantIcon,
    ReceiptLong as ReceiptLongIcon,
    LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
    ordersAPI,
    categoryAPI,
    productAPI,
    tableAPI,
    customersAPI,
} from '../../services/apiService';
import './EditOrderDialog.css';

function EditOrderDialog({ open, orderId, taxPercent, onClose, onSuccess, onError }) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();

    const [categories, setCategories] = useState({});
    const [products, setProducts] = useState({});
    const [tables, setTables] = useState({});
    const [customers, setCustomers] = useState([]);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [showAddProduct, setShowAddProduct] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);

    const [selectedTable, setSelectedTable] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('preparing');
    const [note, setNote] = useState('');

    const [customerPhone, setCustomerPhone] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerId, setCustomerId] = useState(null);

    const [customerPhoneDialog, setCustomerPhoneDialog] = useState(false);
    const [customerNameDialog, setCustomerNameDialog] = useState(false);
    const [creditConfirmDialog, setCreditConfirmDialog] = useState(false);

    const [pendingCredit, setPendingCredit] = useState(0);
    const [customerChecking, setCustomerChecking] = useState(false);
    const [customerCreating, setCustomerCreating] = useState(false);

    const [discount, setDiscount] = useState(0);
    const [discountType, setDiscountType] = useState('amount');

    const isDarkMode = theme.palette.mode === 'dark';

    const toEnglishDigits = (value = '') =>
        value
            .toString()
            .replace(/[۰-۹]/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))
            .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit));

    const toPersianDigits = (value = '') =>
        value.toString().replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[digit]);

    const formatDigitsByLanguage = (value = '') =>
        i18n.language === 'fa' ? toPersianDigits(value) : value;

    const formatMoney = (value = 0) => {
        const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';

        return Number(value || 0).toLocaleString(locale);
    };

    const normalizeItem = (item) => ({
        item: Number(item.item),
        item_id: item.item_id,
        item_title: item.item_title,
        item_price: Number(item.item_price || 0),
        quantity: Number(item.quantity || 0),
        total_price: Number(item.total_price || 0),
    });

    const getActiveItems = () => selectedItems.filter((item) => Number(item.quantity) > 0);

    const resetState = () => {
        setSelectedCategory('');
        setSelectedProduct('');
        setSelectedItems([]);
        setSelectedTable('');
        setSelectedStatus('pending');
        setNote('');
        setShowAddProduct(false);
        setCustomerId(null);
        setCustomerName('');
        setCustomerPhone('');
        setDiscount(0);
        setDiscountType('amount');
        setPendingCredit(0);
        setCustomerPhoneDialog(false);
        setCustomerNameDialog(false);
        setCreditConfirmDialog(false);
    };

    const fetchData = useCallback(async () => {
        if (!orderId) return;

        setLoading(true);

        try {
            const [orderResponse, categoriesData, productsData, tablesData, customersData] =
                await Promise.all([
                    ordersAPI.getById(orderId),
                    categoryAPI.getAll(),
                    productAPI.getAll(),
                    tableAPI.getAll(),
                    customersAPI.getAll(),
                ]);

            const orderData = orderResponse?.data || orderResponse || {};
            const customersList = customersData?.data || customersData || [];

            setCategories(categoriesData || {});
            setProducts(productsData || {});
            setTables(tablesData || {});
            setCustomers(Array.isArray(customersList) ? customersList : []);

            setDiscount(Number(orderData?.discount || 0));

            const normalizedItems = Array.isArray(orderData?.items)
                ? orderData.items.map(normalizeItem)
                : [];

            setSelectedItems(normalizedItems);
            setSelectedStatus(orderData?.status || 'pending');
            setNote(orderData?.note || '');

            const rawCustomerId =
                typeof orderData?.customer === 'object'
                    ? orderData.customer?.id
                    : orderData?.customer;

            setCustomerId(rawCustomerId || null);

            const matchedCustomer = customersList.find(
                (customer) => Number(customer.id) === Number(rawCustomerId)
            );

            setCustomerName(matchedCustomer?.full_name || orderData?.customer_name || '');

            setCustomerPhone(
                matchedCustomer?.phone_number || orderData?.customer_phone_number || ''
            );

            const matchedTableEntry = Object.entries(tablesData || {}).find(
                ([, table]) =>
                    String(table.table_id) === String(orderData?.table_id) ||
                    String(table.id) === String(orderData?.table) ||
                    String(table.pk) === String(orderData?.table)
            );

            setSelectedTable(matchedTableEntry ? matchedTableEntry[0] : '');
        } catch (error) {
            console.error('Error fetching order data:', error);

            onError?.(
                error?.response?.data?.message || error?.message || t('failed_to_load_order')
            );
        } finally {
            setLoading(false);
        }
    }, [orderId, t, onError]);

    useEffect(() => {
        if (open && orderId) {
            fetchData();
        }
    }, [open, orderId, fetchData]);

    const subtotal = useMemo(
        () =>
            selectedItems.reduce(
                (sum, item) => sum + Number(item.item_price || 0) * Number(item.quantity || 0),
                0
            ),
        [selectedItems]
    );

    const discountAmount = useMemo(() => {
        const value = Math.max(0, Number(discount || 0));

        if (discountType === 'percent') {
            return Math.min(subtotal, (subtotal * value) / 100);
        }

        return Math.min(value, subtotal);
    }, [discount, discountType, subtotal]);

    const taxableAmount = useMemo(
        () => Math.max(0, subtotal - discountAmount),
        [subtotal, discountAmount]
    );

    const taxAmount = useMemo(
        () => (taxableAmount * Number(taxPercent || 0)) / 100,
        [taxableAmount, taxPercent]
    );

    const finalTotal = useMemo(() => taxableAmount + taxAmount, [taxableAmount, taxAmount]);

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleCheckCustomer = async () => {
        const phone = toEnglishDigits(customerPhone).replace(/\D/g, '').slice(0, 11);

        if (phone.length !== 11) {
            return;
        }

        setCustomerChecking(true);

        try {
            const res = await customersAPI.getById(phone);
            const customer = res?.data || res;

            setCustomerId(customer?.id || '');
            setCustomerName(customer?.full_name || '');
            setCustomerPhone(customer?.phone_number || phone);

            const credit = Number(customer?.credit || 0);

            if (credit > 0) {
                setPendingCredit(credit);
                setCreditConfirmDialog(true);
            }

            setCustomerPhoneDialog(false);
        } catch (error) {
            if (
                error?.response?.status === 404 ||
                error?.response?.data?.error === 'Customer not found'
            ) {
                setCustomerPhoneDialog(false);
                setCustomerNameDialog(true);
            } else {
                onError?.(t('failed_to_check_customer'));
            }
        } finally {
            setCustomerChecking(false);
        }
    };

    const applyCredit = () => {
        const appliedDiscount = Math.min(pendingCredit, subtotal);

        setDiscount(appliedDiscount);
        setCreditConfirmDialog(false);
    };

    const handleCreateCustomer = async () => {
        const phone = toEnglishDigits(customerPhone).replace(/\D/g, '').slice(0, 11);

        if (!customerName.trim()) {
            return;
        }

        setCustomerCreating(true);

        try {
            const payload = {
                full_name: customerName.trim(),
                phone_number: phone,
                credit: 0,
            };

            const res = await customersAPI.create(payload);
            const customer = res?.data || res;

            setCustomerId(customer?.id || '');
            setCustomerName(customer?.full_name || '');
            setCustomerPhone(customer?.phone_number || phone);
            setCustomerNameDialog(false);
        } catch (error) {
            console.error('Error creating customer:', error);
            onError?.(t('failed_to_create_customer'));
        } finally {
            setCustomerCreating(false);
        }
    };

    const handleProductChange = (event) => {
        const productId = Number(event.target.value);

        setSelectedProduct(productId);

        if (!productId) {
            return;
        }

        const product = Object.values(products).find((item) => Number(item.id) === productId);

        if (!product) {
            return;
        }

        setSelectedItems((previousItems) => {
            const existingIndex = previousItems.findIndex(
                (item) => Number(item.item) === productId
            );

            if (existingIndex !== -1) {
                return previousItems.map((item, index) => {
                    if (index !== existingIndex) {
                        return item;
                    }

                    const newQuantity = Number(item.quantity) + 1;

                    return {
                        ...item,
                        quantity: newQuantity,
                        total_price: newQuantity * Number(item.item_price),
                    };
                });
            }

            return [
                ...previousItems,
                {
                    item: Number(product.id),
                    item_id: product.item_id,
                    item_title: product.title,
                    item_price: Number(product.price || 0),
                    quantity: 1,
                    total_price: Number(product.price || 0),
                },
            ];
        });

        setSelectedProduct('');
    };

    const handleQuantityChange = (itemValue, delta) => {
        setSelectedItems((previousItems) =>
            previousItems.map((item) => {
                if (Number(item.item) !== Number(itemValue)) {
                    return item;
                }

                const newQuantity = Math.max(0, Number(item.quantity) + delta);

                return {
                    ...item,
                    quantity: newQuantity,
                    total_price: newQuantity * Number(item.item_price),
                };
            })
        );
    };

    const handleRemoveItem = (itemValue) => {
        setSelectedItems((previousItems) =>
            previousItems.map((item) => {
                if (Number(item.item) !== Number(itemValue)) {
                    return item;
                }

                return {
                    ...item,
                    quantity: 0,
                    total_price: 0,
                };
            })
        );
    };

    const handleDiscountChange = (event) => {
        const value = Math.max(0, Number(event.target.value || 0));

        if (discountType === 'percent') {
            setDiscount(Math.min(value, 100));
            return;
        }
        setDiscount(Math.min(value, subtotal));
    };

    const handleSubmit = async () => {
        const payload = {
            status: selectedStatus,
            note,
            customer: customerId,
            discount: discountAmount,
        };

        if (selectedTable) {
            const selectedTableObj = tables[selectedTable];

            payload.table_id = selectedTableObj?.table_id || selectedTable;
        }

        const activeItems = getActiveItems();

        if (selectedItems.length > 0) {
            payload.items = selectedItems.map((item) => ({
                item: Number(item.item),
                item_id: item.item_id,
                item_title: item.item_title,
                quantity: Number(item.quantity),
            }));
        }

        setSubmitting(true);

        try {
            await ordersAPI.update(orderId, payload);

            onSuccess?.(t('order_updated_successfully'));
            handleClose();
        } catch (error) {
            console.error('Error updating order:', error);

            onError?.(error?.response?.data?.message || t('failed_to_update_order'));
        } finally {
            setSubmitting(false);
        }
    };

    const filteredProducts = Object.entries(products).filter(([, product]) => {
        if (!selectedCategory) {
            return true;
        }

        return Number(product.item_type) === Number(selectedCategory);
    });

    const statusLabels = {
        pending: t('order_status.pending'),
        preparing: t('order_status.preparing'),
        served: t('order_status.served'),
        paid: t('order_status.paid'),
        cancelled: t('order_status.cancelled'),
    };
    const getDiscountAdornment = () => {
        if (discountType === 'percent') {
            return '%';
        }

        if (i18n.language === 'fa') {
            return 'تومان';
        }
        return '';
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                className="edit-order-dialog"
            >
                <DialogTitle className="edit-order-dialog__title">
                    <Box className="edit-order-dialog__title-content">
                        <Box className="edit-order-dialog__title-icon">
                            <ReceiptLongIcon />
                        </Box>

                        <Box className="edit-order-dialog__title-text">
                            <Typography component="h2" className="edit-order-dialog__heading">
                                {t('edit_order')}
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        aria-label={t('close')}
                        className="edit-order-dialog__close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers className="edit-order-dialog__content">
                    {loading ? (
                        <Box className="edit-order-dialog__loading">
                            <Typography>{t('loading')}</Typography>
                        </Box>
                    ) : (
                        <Box className="edit-order-dialog__body">
                            <section className="edit-order-section">
                                <Box className="edit-order-section__header">
                                    <Box className="edit-order-section__heading">
                                        <PersonIcon />
                                        <Typography component="h3">{t('customer')}</Typography>
                                    </Box>

                                    <Chip
                                        size="small"
                                        label={customerId ? t('customer') : t('walk_in_customer')}
                                    />
                                </Box>

                                <Box className="customer-card">
                                    <Box className="customer-card__identity">
                                        <Box className="customer-card__avatar">
                                            <PersonIcon />
                                        </Box>

                                        <Box className="customer-card__details">
                                            <Typography className="customer-card__name">
                                                {customerName || t('walk_in_customer')}
                                            </Typography>

                                            {customerPhone && (
                                                <Typography className="customer-card__phone">
                                                    {formatDigitsByLanguage(customerPhone)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="outlined"
                                        onClick={() => setCustomerPhoneDialog(true)}
                                        className="customer-card__change"
                                    >
                                        {t('change_customer')}
                                    </Button>
                                </Box>
                            </section>

                            <section className="edit-order-section">
                                <Box className="edit-order-section__header">
                                    <Box className="edit-order-section__heading">
                                        <ReceiptLongIcon />
                                        <Typography component="h3">{t('order_items')}</Typography>
                                    </Box>

                                    <Chip
                                        size="small"
                                        label={`${formatDigitsByLanguage(
                                            getActiveItems().length
                                        )} ${t('items')}`}
                                    />
                                </Box>

                                {getActiveItems().length === 0 ? (
                                    <Box className="empty-items">
                                        <ReceiptLongIcon />
                                        <Typography>
                                            {t('no_items') || 'No items in this order'}
                                        </Typography>

                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => setShowAddProduct(true)}
                                        >
                                            {t('add_new_product')}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box className="order-items">
                                        {getActiveItems().map((item) => (
                                            <Card
                                                key={item.item}
                                                className="order-item-card"
                                                elevation={0}
                                            >
                                                <CardContent className="order-item-card__content">
                                                    <Box className="order-item-card__info">
                                                        <Typography className="order-item-card__name">
                                                            {item.item_title}
                                                        </Typography>

                                                        <Typography className="order-item-card__price">
                                                            {formatMoney(item.item_price)}
                                                        </Typography>
                                                    </Box>

                                                    <Box className="order-item-card__controls">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleQuantityChange(item.item, -1)
                                                            }
                                                            aria-label={t('decrease_quantity')}
                                                            className="quantity-button"
                                                        >
                                                            <RemoveIcon />
                                                        </IconButton>

                                                        <Typography className="quantity-value">
                                                            {formatDigitsByLanguage(item.quantity)}
                                                        </Typography>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleQuantityChange(item.item, 1)
                                                            }
                                                            aria-label={t('increase_quantity')}
                                                            className="quantity-button"
                                                        >
                                                            <AddIcon />
                                                        </IconButton>
                                                    </Box>

                                                    <Typography className="order-item-card__total">
                                                        {formatMoney(
                                                            Number(item.item_price) *
                                                                Number(item.quantity)
                                                        )}
                                                    </Typography>

                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveItem(item.item)}
                                                        aria-label={t('delete')}
                                                        className="order-item-card__remove"
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                )}

                                <Button
                                    variant="text"
                                    startIcon={showAddProduct ? <CloseIcon /> : <AddIcon />}
                                    onClick={() => setShowAddProduct((previous) => !previous)}
                                    className="add-product-button"
                                >
                                    {showAddProduct ? t('close') : t('add_new_product')}
                                </Button>

                                {showAddProduct && (
                                    <Box className="add-product-panel">
                                        <Typography className="add-product-panel__title">
                                            {t('add_new_product')}
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t('category')}</InputLabel>

                                                    <Select
                                                        value={selectedCategory}
                                                        onChange={(event) =>
                                                            setSelectedCategory(event.target.value)
                                                        }
                                                        label={t('category')}
                                                    >
                                                        <MenuItem value="">
                                                            {t('all_categories_label')}
                                                        </MenuItem>

                                                        {Object.entries(categories).map(
                                                            ([id, category]) => (
                                                                <MenuItem
                                                                    key={id}
                                                                    value={category.id}
                                                                >
                                                                    {category.title}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{t('Product')}</InputLabel>

                                                    <Select
                                                        value={selectedProduct}
                                                        onChange={handleProductChange}
                                                        label={t('Product')}
                                                    >
                                                        {filteredProducts.map(([id, product]) => (
                                                            <MenuItem key={id} value={product.id}>
                                                                {product.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </section>

                            <section className="edit-order-section">
                                <Box className="edit-order-section__header">
                                    <Box className="edit-order-section__heading">
                                        <TableRestaurantIcon />
                                        <Typography component="h3">{t('order_details')}</Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('tables')}</InputLabel>

                                            <Select
                                                value={selectedTable}
                                                onChange={(event) =>
                                                    setSelectedTable(event.target.value)
                                                }
                                                label={t('tables')}
                                            >
                                                <MenuItem value="">{t('take_away_order')}</MenuItem>

                                                {Object.entries(tables).map(([id, table]) => (
                                                    <MenuItem key={id} value={id}>
                                                        {table.table_id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('status')}</InputLabel>

                                            <Select
                                                value={selectedStatus}
                                                onChange={(event) =>
                                                    setSelectedStatus(event.target.value)
                                                }
                                                label={t('status')}
                                            >
                                                {Object.entries(statusLabels).map(
                                                    ([status, label]) => (
                                                        <MenuItem key={status} value={status}>
                                                            {label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={t('order_note')}
                                    value={note}
                                    onChange={(event) => setNote(event.target.value)}
                                    className="order-note-field"
                                />
                            </section>

                            <section className="edit-order-section">
                                <Box className="edit-order-section__header">
                                    <Box className="edit-order-section__heading">
                                        <LocalOfferIcon />
                                        <Typography component="h3">
                                            {t('payment_summary')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box className="payment-summary">
                                    <Box className="payment-summary__row">
                                        <Typography>{t('subtotal')}</Typography>

                                        <Typography>{formatMoney(subtotal)}</Typography>
                                    </Box>

                                    <Box className="payment-summary__row payment-summary__discount">
                                        <Typography>{t('discount')}</Typography>

                                        <Box className="discount-control">
                                            <TextField
                                                value={discount}
                                                onChange={handleDiscountChange}
                                                size="small"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {getDiscountAdornment()}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                className="discount-field"
                                            />
                                            <FormControl
                                                component="fieldset"
                                                className="discount-type-field"
                                            >
                                                <RadioGroup
                                                    row
                                                    value={discountType}
                                                    onChange={(event) => {
                                                        setDiscountType(event.target.value);
                                                        setDiscount(0);
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="amount"
                                                        control={<Radio size="small" />}
                                                        label={
                                                            i18n.language === 'fa'
                                                                ? 'مبلغ'
                                                                : 'Amount'
                                                        }
                                                    />

                                                    <FormControlLabel
                                                        value="percent"
                                                        control={<Radio size="small" />}
                                                        label={
                                                            i18n.language === 'fa'
                                                                ? 'درصد'
                                                                : 'Percent'
                                                        }
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                    </Box>

                                    <Divider />

                                    <Box className="payment-summary__row">
                                        <Typography>{t('tax')}</Typography>

                                        <Typography>{formatMoney(taxableAmount)}</Typography>
                                    </Box>
                                    <Box className="payment-summary__row">
                                        <Typography>{t('discount')}</Typography>

                                        <Typography>
                                            <Typography className="discount-applied">
                                                {formatMoney(discountAmount)}
                                            </Typography>
                                        </Typography>
                                    </Box>

                                    <Box className="payment-summary__row payment-summary__tax">
                                        <Typography>
                                            {t('tax')} (
                                            {formatDigitsByLanguage(Number(taxPercent || 0))}
                                            %)
                                        </Typography>

                                        <Typography>+{formatMoney(taxAmount)}</Typography>
                                    </Box>

                                    <Divider />

                                    <Box className="payment-summary__total">
                                        <Typography>{t('final_total')}</Typography>

                                        <Typography>{formatMoney(finalTotal)}</Typography>
                                    </Box>
                                </Box>
                            </section>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className="edit-order-dialog__actions">
                    <Button onClick={handleClose} variant="outlined" disabled={submitting}>
                        {t('cancel')}
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting || loading}
                        className="save-order-button"
                    >
                        {submitting ? t('saving') : t('save_changes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={customerPhoneDialog}
                onClose={() => setCustomerPhoneDialog(false)}
                className="edit-order-dialog"
            >
                <DialogTitle>{t('enter_customer_phone')}</DialogTitle>

                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        sx={{ mt: 1 }}
                        label={t('phone_number')}
                        value={customerPhone}
                        onChange={(event) => setCustomerPhone(event.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCustomerPhoneDialog(false)}>{t('cancel')}</Button>

                    <Button
                        onClick={handleCheckCustomer}
                        variant="contained"
                        disabled={customerChecking}
                    >
                        {customerChecking ? t('checking') : t('confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={customerNameDialog}
                onClose={() => setCustomerNameDialog(false)}
                className="edit-order-dialog"
            >
                <DialogTitle>{t('new_customer')}</DialogTitle>

                <DialogContent>
                    <Typography sx={{ mb: 2 }}>{t('customer_not_found_enter_name')}</Typography>

                    <TextField
                        autoFocus
                        fullWidth
                        label={t('full_name')}
                        value={customerName}
                        onChange={(event) => setCustomerName(event.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCustomerNameDialog(false)}>{t('cancel')}</Button>

                    <Button
                        onClick={handleCreateCustomer}
                        variant="contained"
                        disabled={customerCreating}
                    >
                        {customerCreating ? t('saving') : t('save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={creditConfirmDialog}
                onClose={() => setCreditConfirmDialog(false)}
                className="edit-order-dialog"
            >
                <DialogTitle>{t('use_customer_credit')}</DialogTitle>

                <DialogContent>
                    <Typography>
                        {t('customer_has_credit_message', {
                            amount: formatDigitsByLanguage(pendingCredit),
                        })}
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setCreditConfirmDialog(false)}>{t('no')}</Button>

                    <Button onClick={applyCredit} variant="contained" color="success">
                        {t('yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default EditOrderDialog;
